import { mergeDeepLeft } from 'ramda'
import { DataTableResponse } from '../services/DatatableService'
import LegacyAPI from './legacy_api'

declare const Ladda: { create: (el: HTMLElement) => any }

export default class CSVExport {
    button: JQuery<HTMLElement> | null = null
    title: string
    filename: string = ''
    remote: boolean = false
    json?: DataTableResponse
    columns?: any
    route?: string
    attributes?: any
    ladda: any | null = null
    status: number
    id?: string
    auto_open: boolean
    params?: any

    /**
     * Add CSV export capability to datatable using JS only
     */
    constructor({
        button,
        title,
        id,
        columns,
        remote,
        route,
        params,
    }: {
        title: string
        button?: string
        id?: string
        json?: DataTableResponse
        columns?: any
        remote?: boolean
        route?: string
        params?: any
    }) {
        this.title = title // First header in thead>th (extract from JSON response?)
        this.status = 200
        this.auto_open = false
        this.attributes = {}
        if (button) this.button = $(button)
        if (params) this.params = params
        if (id) this.id = id
        if (remote !== undefined) this.remote = remote
        if (route) this.route = route
        if (columns) this.columns = columns
    }

    get(attr: string) {
        return this.attributes[attr]
    }

    setJson(json: DataTableResponse) {
        this.json = json
    }

    setParams(params: any) {
        this.params = params
    }

    static watch(ele: any) {
        const t = ele
        if (typeof t.report === 'undefined') {
            const $element = $(t)
            t.report = new CSVExport({
                title: 'calls',
                id: $element.data('id'),
                button: ele,
                route: '/reports/calls',
            })
            t.report.createLadda().setProgress(0)
            t.report.laddaStart()
            $element.addClass('ladda-active')

            // poll the report for progress
            t.report.waitForFile({
                complete() {
                    $element.attr('class', '')
                    $element.html(`
                        <a
                            class="btn btn-sm btn-light btn-icon-only datatable-tooltip"
                            href="${t.report.get('file_upload_url')}"
                            target="_blank"
                            title="Download CSV"
                            download>
                            <span><i class="fa-solid fa-download"></i></span>
                        </a>
                    `)
                },
            })
        }
    }

    downloadRemote() {
        // Workflow:
        // - UI = Downloading...
        // - POST to /reports/charges.json
        //     - store attributes
        //         - returns ID
        //         - OR [errors]
        // - calls openFileWhenReady
        //     - if in progress
        //         - calls waitForFile
        //             - EVERY 2000ms
        //             - GET to /reports/charges/25.json
        //             - store attributes
        //         - PUT to ID -> /reports/charges/25.json?report%5Brecipients_csv%5D=
        //     - else if done
        //         - openFile (aka download it)
        //         - if auto_open
        //         - PUT w/ recipients_csv
        // - UI = Done...

        // only allow one download per report
        if (!this.inProgress()) {
            this.buttonStart()
            this.auto_open = true
            this.create()
        }
    }

    // Generate CSV locally
    // --------------------------------------------------------

    /**
     * Generate CSV file in browser and download
     */
    download(csv: any) {
        // CSV File
        const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' })

        // Download link
        const downloadLink = document.createElement('a')

        // File name
        downloadLink.download = this.filename

        // We have to create a link to the file
        downloadLink.href = window.URL.createObjectURL(csvFile)

        // Make sure that the link is not displayed
        downloadLink.style.display = 'none'

        // Add the link to your DOM and click
        document.body.appendChild(downloadLink)
        downloadLink.click()
    }

    /**
     * Convert JSON response to CSV rows
     */
    parse(json: DataTableResponse): string {
        function sanitize_text(col) {
            // 1) Make sure column is converted to String
            // 2) Sanitize text for commas, new lines, etc which might break CSV parsing
            return `${col}`
                .replace(/\\rn/g, '')
                .replace(/\\r/g, '')
                .replace(/\\n/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/(\d*),(\d+)/g, '$1$2')
                .replace(/,/g, ' ')
                .replace(/ +(?= )/g, '')
        }

        const { data } = json

        if (data.length === 0) {
            return ''
        }

        const rows: string[] = []

        // Append headers
        rows.push(this.headers().join(','))

        // Extract only the pre-defined list of columns (skipping URLs)
        data.forEach((row: string[]) => {
            const parsed_row = this.keys().map((key) => sanitize_text(row[key]))
            rows.push(parsed_row.join(','))
        })

        return rows.join('\n')
    }

    generate() {
        // 0. returns promise so loading state can be communicated
        // 1. externally: fetchs API but this time with max count (and all the same filters)
        return new Promise<void>(async (resolve, reject) => {
            if (!this.json) {
                reject(new Error('No JSON response provided'))
                return
            }

            // 2. extracts values from JSON response from hardcoded column/key list
            // 3. build CSV with headers + rows
            const csv = this.parse(this.json)

            // 4. generate filename
            this.filename = this.generateFilename(this.title)

            // 5. downloads file
            this.download(csv)
            resolve()
        })
    }

    /**
     * List of exportable headers
     */
    headers(): string[] {
        return Object.values(this.columns)
    }

    /**
     * List of columns/keys to extract from JSON
     */
    keys(): string[] {
        return Object.keys(this.columns)
    }

    /**
     * Generate filename, for ex:
     *
     *    call_endpoints_2021-01-01.csv
     *
     */
    generateFilename(title: string): string {
        const filename = title.split('/')
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '-')
        const base = [`${filename[filename.length - 1].replace('.json', '')}s`, date].join('_')
        return `${base}.csv`
    }

    // API Endpoint
    // --------------------------------------------------------

    routes() {
        const t = this
        return {
            create() {
                return `${t.route}.json`
            },

            update(params: any) {
                return `${t.route}/${t.id}.json?${$.param(params)}`
            },

            get() {
                return `${t.route}/${t.id}.json`
            },
        }
    }

    paramsAll(opts?: any) {
        return mergeDeepLeft(opts || {}, {
            params: this.params,
            format: 'csv',
        })
    }

    create() {
        const defaultParams = {
            notify_user: true,
        }
        const t = this
        $.ajax({
            dataType: 'json',
            type: 'post',
            url: this.routes().create(),
            data: { report: t.paramsAll(defaultParams) },
            complete(xhr) {
                t.updateAttributes(xhr, t.paramsAll())
            },
        })
    }

    update(report_params: any) {
        if (!this.id) {
            console.error('CSVExport: ID is not defined, canceling update', this.id)
            return
        }
        const t = this
        $.ajax({
            dataType: 'json',
            type: 'PUT',
            url: this.routes().update({ report: this.paramsAll(report_params) }),
            complete(xhr) {
                if (xhr.status !== 200) {
                    t.attributes.errors = xhr.responseJSON.errors
                    t.updateErrors()
                    t.laddaStop()
                }
            },
        })
    }

    inProgress() {
        return typeof this.attributes === 'object' && !!this.attributes.in_progress
    }

    updateErrors() {
        if (typeof this.attributes === 'object' && typeof this.attributes.errors !== 'undefined') {
            Object.keys(this.attributes.errors).forEach((key) => {
                const error = this.attributes.errors[key]
                LegacyAPI.flashNotice(error, 'danger')
            })
        } else if (typeof this.attributes === 'undefined') {
            LegacyAPI.flashNotice('An unknown error has occurred.', 'danger')
        }
    }

    openFileWhenReady(options) {
        if (typeof this.attributes !== 'undefined') {
            if (this.inProgress() || typeof this.attributes.file_upload_url === 'undefined') {
                this.waitForFile(options)
            } else if (this.auto_open === true) {
                // stop the notification email from being sent to the user
                this.update({
                    recipients_csv: '',
                })
                // download the file inline
                this.openFile(this.attributes.file_upload_url)
            }
        }
    }

    waitForFile(options) {
        if (this.statusOK()) {
            const t = this
            setTimeout(() => {
                $.ajax({
                    dataType: 'json',
                    type: 'get',
                    url: t.routes().get(),
                    complete(xhr) {
                        t.updateAttributes(xhr, options)
                    },
                })
            }, 1000)
        }
    }

    openFile(file_url) {
        const iframe = document.createElement('iframe')
        iframe.style.height = '0px'
        iframe.style.width = '0px'
        iframe.style.border = '0px'
        iframe.src = file_url
        window.callpixels.content.append($(iframe))
    }

    statusOK() {
        return this.status === 200 || this.status === 201
    }

    updateAttributes(xhr, options) {
        this.attributes = xhr.responseJSON
        this.id = xhr.responseJSON.id

        this.status = xhr.status
        this.updateErrors()
        this.updateLadda()

        if (this.status !== 200) {
            console.error('CSVExport: server request errored, skipping export')
            return
        }

        this.openFileWhenReady(options)

        // callback on complete
        if (!this.inProgress()) {
            if (typeof options !== 'undefined') {
                if (typeof options.complete === 'function') {
                    options.complete(self)
                }
            }
        }
        // callback on every ping
        if (typeof options !== 'undefined') {
            if (typeof options.ping === 'function') {
                options.ping(self)
            }
        }
    }

    // Ladda/Button UI
    // --------------------------------------------------------

    buttonStart() {
        this.button?.addClass('is-disabled')
    }

    buttonDone() {
        this.button?.removeClass('is-disabled')
    }

    createLadda() {
        const button = this.button
        if (!button) {
            console.error('CSVExport: button is not defined, cannot create Ladda')
            return this.ladda
        }

        if (!this.ladda) {
            if (!button.attr('data-spinner-color')) {
                button.attr('data-spinner-color', this.defaultSpinnerColor())
            }
            button.attr('data-style', 'slide-left')
            button.attr('data-size', 'xs')

            const el = button.get(0)
            if (el) {
                this.ladda = Ladda.create(el)
                this.ladda.button = button
                this.updateLadda()
            } else {
                console.error('Ladda element not found', el)
            }
        }
        return this.ladda
    }

    defaultSpinnerColor() {
        const root = document.documentElement
        const theme = root?.getAttribute('data-bs-theme')
        const isDark = theme === 'dark' || root?.classList.contains('dark')
        return isDark ? '#fff' : '#333'
    }

    laddaStart() {
        if (!this.ladda) {
            this.createLadda()
        }
        const ladda = this.ladda
        if (!ladda) return
        this.buttonStart()
        ladda.button?.addClass('ladda-button')
        ladda.start()
    }

    laddaStop() {
        const ladda = this.ladda
        if (!ladda) return
        this.buttonDone()
        ladda.stop()
    }

    updateLadda() {
        const ladda = this.ladda
        if (!ladda) return
        if (!this.inProgress()) {
            this.laddaStop()
        } else if (!ladda.isLoading()) {
            this.laddaStart()
        }
        if (typeof this.attributes !== 'undefined') {
            ladda.setProgress(this.attributes.export_percentage / 100)
        }
    }
}
